import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Flex, Popover, Spinner, Text, TextArea, TextField } from '@radix-ui/themes'
import { useForm } from 'react-hook-form'
import { createSiteSchema, siteSchema, SiteSchema } from '~/features/sites/schemas'
import { useTranslation } from 'react-i18next'
import styles from './SiteForm.module.css'
import { useCurrentOrg } from '~/features/orgnisations/hooks/useCurrentOrg'
import * as React from 'react'
import { Command } from 'cmdk'
import { LuSearch } from 'react-icons/lu'
import { useCurrentLanguage } from '~/hooks/useCurrentLanguage'
import { useDebounceValue } from 'usehooks-ts'
import { fetchLocationSuggestions, LocationSuggestionFeature } from '../service'
import { useAsync } from 'react-use'

type CommonProps = {
  isPending: boolean
  site?: SiteSchema
}

type SiteFormProps = CommonProps & (CreateSiteFormProps | EditSiteFormProps)

const SiteForm = ({ isPending, onSubmit, site }: SiteFormProps) => {
  const { t } = useTranslation('sites')
  const { id: orgID } = useCurrentOrg()

  const {
    register,
    handleSubmit,
    formState: { isValid, isDirty },
    setValue
  } = useForm<SiteSchema>({
    resolver: zodResolver(site ? siteSchema : createSiteSchema),
    defaultValues: site ?? { orgID, coordinates: null },
    mode: 'onChange'
  })

  const [addressInput, setAddressInput] = React.useState(site?.address || '')
  const [open, setOpen] = React.useState(false)
  const language = useCurrentLanguage()

  const [locationSuggestions, setLocationSuggestions] = React.useState<LocationSuggestionFeature[]>([])
  const [isSearching, setIsSearching] = React.useState(false)

  const [debouncedAddress] = useDebounceValue(addressInput, 500)

  useAsync(async () => {
    if (debouncedAddress && debouncedAddress.length < 3) {
      setLocationSuggestions([])
      return
    }
    const suggestions = await fetchLocationSuggestions(debouncedAddress)
    setIsSearching(false)
    setLocationSuggestions(suggestions)
  }, [debouncedAddress, language])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.formContainer}>
        <Flex gap="6" direction="column" mt={'4'}>
          <Flex gap={'5'} className={styles.formRow}>
            <Flex direction={'column'} gap="4" className={styles.formColumn}>
              <Flex direction={'column'} gap="1">
                <Text as="label" htmlFor="name" size="2" weight={'medium'}>
                  {t('sites.form.fields.name.label')}
                </Text>
                <TextField.Root
                  id="name"
                  variant="surface"
                  placeholder={t('sites.form.fields.name.placeholder')}
                  {...register('name')}
                />
              </Flex>
              <Flex direction={'column'} gap="1">
                <Text as="label" htmlFor="address" size="2" weight={'medium'}>
                  {t('sites.form.fields.address.label')}
                </Text>
                <Popover.Root open={open} onOpenChange={setOpen}>
                  <Popover.Trigger>
                    <TextField.Root id="address" {...register('address')} />
                  </Popover.Trigger>
                  <Popover.Content className="p-0" side="bottom">
                    <Command className="w-full" loop shouldFilter={false}>
                      <Flex align="center" gap="1" p="1" className="border-b border-gray-4">
                        <LuSearch className="ml-2 h-4 w-4 text-gray-9" />
                        <Command.Input
                          value={addressInput}
                          autoFocus
                          onValueChange={(s) => {
                            setIsSearching(s.length > 3)
                            setAddressInput(s)
                          }}
                          placeholder={t('sites.form.fields.address.placeholder')}
                          className="text-sm w-full py-2 px-1 outline-none border-none focus:border-none focus:shadow-none focus:ring-0 bg-transparent"
                          autoComplete="off"
                        />
                      </Flex>
                      <Command.List>
                        {isSearching && (
                          <Command.Loading>
                            <Flex justify="center" align="center" className="p-3">
                              <Spinner className="w-4 h-4 text-gray-9" />
                            </Flex>
                          </Command.Loading>
                        )}
                        {!isSearching && locationSuggestions.length === 0 && (
                          <Command.Empty>
                            <Flex direction={'column'} justify="center" align="center" gap={'2'} p="4">
                              {addressInput ? (
                                <>
                                  <Text size="2" className="mr-2" color="gray">
                                    Aucune adresse correspondante trouvée
                                  </Text>
                                  <Button
                                    size="1"
                                    variant="soft"
                                    onClick={() => {
                                      setValue('address', addressInput, {
                                        shouldValidate: true,
                                        shouldDirty: true
                                      })
                                      setOpen(false)
                                    }}
                                  >
                                    Utiliser malgré tout
                                  </Button>
                                </>
                              ) : (
                                <Text size="2" className="mr-2" color="gray">
                                  Commencez à taper pour rechercher une adresse
                                </Text>
                              )}
                            </Flex>
                          </Command.Empty>
                        )}
                        {locationSuggestions.map((sug) => (
                          <Command.Item
                            className="flex items-center p-2 cursor-pointer hover:bg-gray-3 focus:bg-gray-3 data-[selected=true]:bg-gray-3"
                            key={sug.properties.id}
                            value={sug.properties.label}
                            onSelect={async (suggestion) => {
                              setValue('address', suggestion, { shouldValidate: true, shouldDirty: true })
                              setValue(
                                'coordinates',
                                { lat: sug.geometry.coordinates[1], lng: sug.geometry.coordinates[0] },
                                { shouldValidate: true, shouldDirty: true }
                              )
                              setOpen(false)
                            }}
                          >
                            {sug.properties.label}
                          </Command.Item>
                        ))}
                      </Command.List>
                    </Command>
                  </Popover.Content>
                </Popover.Root>
              </Flex>
              <Flex direction={'column'} gap="1">
                <Text as="label" htmlFor="internalID" size="2" weight={'medium'}>
                  {t('sites.form.fields.internalID.label')}
                </Text>
                <Text as="p" size="1" color="gray">
                  {t('sites.form.fields.internalID.description')}
                </Text>
                <TextField.Root
                  id="internalID"
                  variant="surface"
                  placeholder={t('sites.form.fields.internalID.placeholder')}
                  {...register('internalID')}
                />
              </Flex>
            </Flex>

            <Flex direction={'column'} gap="4" className={styles.formColumn}>
              <Flex gap="1" direction={'column'} flexGrow={'1'}>
                <Text size="2" weight={'medium'}>
                  {t('sites.form.fields.description.label')}
                </Text>
                <TextArea
                  rows={6}
                  variant="surface"
                  placeholder={t('sites.form.fields.description.placeholder')}
                  {...register('description', { required: false })}
                />
              </Flex>
            </Flex>
          </Flex>
          <Button type="submit" disabled={!isValid || !isDirty} size={'3'} loading={isPending} className="ml-auto">
            {site?.id ? t('sites.form.buttons.update') : t('sites.form.buttons.create')}
          </Button>
        </Flex>
      </div>
    </form>
  )
}

type CreateSiteFormProps = {
  onSubmit: (data: SiteSchema) => void
}

export const CreateSiteForm = (props: CreateSiteFormProps & CommonProps) => {
  return <SiteForm {...props} />
}

type EditSiteFormProps = {
  onSubmit: (data: SiteSchema) => void
  site: SiteSchema
}

export const EditSiteForm = (props: EditSiteFormProps & CommonProps) => {
  return <SiteForm {...props} />
}

export default SiteForm
